import React, { useState } from "react";
import { Download, X, Share, Check } from "lucide-react";
import { useInstallPrompt } from "@/lib/install";

const DISMISS_KEY = "pool_install_dismissed";

export default function InstallBanner() {
  const { available, installed, install, platform } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(Boolean(localStorage.getItem(DISMISS_KEY)));
  const [done, setDone] = useState(false);

  const show = !installed && !dismissed && (available || platform === "ios" || done);
  if (!show) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };
  const onInstall = async () => {
    const outcome = await install();
    if (outcome === "accepted") setDone(true);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[400px] z-50 bg-[#141414] border border-white/15 shadow-2xl shadow-black/60 p-4 flex gap-3 items-start animate-[fadeIn_0.4s_ease]" data-testid="install-banner">
      <img src="/icon-192.png" alt="" className="w-11 h-11 shrink-0" />
      <div className="flex-1 min-w-0">
        {done ? (
          <>
            <div className="font-['Bebas_Neue'] text-xl tracking-wide leading-none flex items-center gap-2">
              <Check className="w-4 h-4 text-[#00C176]" /> Installed
            </div>
            <p className="text-xs text-white/60 mt-1 leading-relaxed" data-testid="install-done-hint">
              Open your app drawer (swipe up) and look for <span className="text-white">Pick Em</span>. Long-press
              the icon and drag it onto your home screen. Not there? Open Chrome's ⋮ menu and tap
              <span className="text-white"> Add to Home screen</span>.
            </p>
          </>
        ) : (
          <>
            <div className="font-['Bebas_Neue'] text-xl tracking-wide leading-none">Add Pick Em to your phone</div>
            {platform === "ios" ? (
              <p className="text-xs text-white/60 mt-1 leading-relaxed" data-testid="install-ios-hint">
                In Safari, tap <Share className="w-3.5 h-3.5 inline -mt-0.5 text-white" /> <span className="text-white">Share</span>, then{" "}
                <span className="text-white">Add to Home Screen</span> for a full-screen app.
              </p>
            ) : (
              <p className="text-xs text-white/60 mt-1">Full-screen app icon, live scores, one tap to your picks.</p>
            )}
          </>
        )}
        <div className="flex items-center gap-3 mt-3">
          {!done && available && (
            <button onClick={onInstall} className="flex items-center gap-1.5 bg-[#E31837] hover:bg-[#C8102E] px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-200" data-testid="install-button">
              <Download className="w-3.5 h-3.5" /> Install
            </button>
          )}
          <button onClick={dismiss} className="text-[11px] uppercase tracking-wider text-white/40 hover:text-white transition-colors duration-200" data-testid="install-dismiss">
            {done ? "Got it" : "Not now"}
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="text-white/40 hover:text-white transition-colors duration-200" aria-label="Dismiss" data-testid="install-close">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
